import React, { useState } from "react";
import { Page } from "../Page";
import { api } from "../../api/api";
import { Table } from "../../core/ui/Table";
import { useConditional } from "../../core/hooks/useConditional";
import { newID } from "../../core/utils/unique";
import { useApi } from "../../core/hooks/useApi";
import { PdfDesigner } from "../../pdf-designer/PdfDesigner";
import { CheckboxCol, CopyToClipboardCol, IDCol, LastUpdatedCol, SyncTableCol, TagsCol, TitleCol, VersionCol } from "../../layout/TableCells";
import { FormModal } from "../../data-entry/FormModal";
import { report_def, ReportStructure } from "../../core/pdf/types";
import { useBulkActions } from "../../core/hooks/useBulkActions";
import { DuplicateModal } from "../../core/ui/DuplicateModal";
import { Modal } from "../../core/ui/Modal";
import { JSEditor } from "../../core/ui/JSEditor";
import { AutoFilterList } from "../../core/ui/AutoFilterList";
import { useAutoFilter } from "../../core/hooks/useAutoFilter";


export function Reports(props: {

}) {

	const [token, setToken] = React.useState("");

	const duplicateModal = useConditional((item: report_def) => {
		return (
			<DuplicateModal
				item={item}
				onDuplicate={(item, newId) => {
					return api.report_def_post({
						...item,
						id: newId,
						title: (item.title || "") + " (Copy)",
					}).then(() => {
						bulk.selection.clear();
						setToken(newID());
					});
				}}
				onClose={() => duplicateModal.close()}
			/>
		);
	});

	const replaceModal = useConditional((item: report_def) => {
		const obj = { data: JSON.parse(JSON.stringify(item.data)) };
		return (
			<ReplaceJsonModal
				obj={obj}
				onCancel={() => replaceModal.close()}
				onSave={() => {
					item.data = obj.data;
					replaceModal.close();
				}}
			/>
		);
	});

	const createModal = useConditional(() => {
		return (
			<FormModal
				form="report-create"
				onCancel={createModal.close}
				onSave={(value: any) => {

					const rep = {
						id: value.id,
						title: "New Report",
						deleted: 0,
						data: {
							id: "@",
							layoutType: value.layoutType,
							template: value.template,
							sections: [
								{
									type: "section",
									content: [{
										type: "p",
										segments: [],
									}],
								}
							],
						},
					};

					api.report_def_post(rep).then(() => {
						setToken(newID());
						createModal.close();
					}).catch(() => {

					});					

				}}
			/>
		)
	});

	const reports = useApi({
		request: () => api.report_def_get_all(),

		prepareResult: (result: report_def[]) => {
			result.sort((a, b) => {
				return (a ? a.title : "").localeCompare(b ? b.title : "");
			});

			return result;
		}
	}, [token]);

	const edit = useConditional((def: report_def) => {
debugger;
		function handleSave(structure: ReportStructure, title: string) {
			def.data = structure;
			def.title = title;
	
			return api.report_def_post(def);
		}
		
		return (
			<PdfDesigner
				def={def}

				onCancel={edit.close}

				onDelete={() => {
					api.report_def_delete(def).then(() => {
						setToken(newID());
						edit.close();
					}).catch(() => {

					})
				}}

				onSave={(structure, title, close) => {
					handleSave(structure, title).then(() => {
						if (close) edit.close();
					}).catch(() => {
			
					});					
				}}

				onPreview={(structure, title) => {
					// first save
					handleSave(structure, title).then(() => {
						// launch preview
						const w = window.open(`/pdf/${encodeURIComponent(def.id)}/`, "pdf-preview");
						w?.focus();

					}).catch(() => {

					});
				}}
			/>
		)
	});

	const { filteredData, onFilterChange } = useAutoFilter(reports);

	const bulk = useBulkActions<report_def>({
		items: filteredData,
		onDelete: (items) => {
			return Promise.all(items.map(item => api.report_def_delete(item))).then(() => {
				setToken(newID());
			});
		},
		onDuplicate: (item) => {
			duplicateModal.show(item);
		},
		onReplace: (item) => {
			replaceModal.show(item);
		},
	});


	return (
		<Page
			compact={true}
			title={{
				text: "Reports",
				icon: "report_color",
				buttons: [
					{ title: "Create" }
				]
			}}
			onButton={(btn) => {
				createModal.show({});
			}}
		>

			<AutoFilterList
				filterID="reports"
				data={reports}
				onChange={onFilterChange}
			/>

			<Table
				cols={[
					CheckboxCol({ selection: bulk.selection, items: filteredData }),
					VersionCol(),
					IDCol(),
					TitleCol(),
					TagsCol(),
					LastUpdatedCol(),
					SyncTableCol({ container: "report_def", onSync: () => {
						setToken(newID());
					} }),
					CopyToClipboardCol(),
				]}
				items={filteredData}
				interactive={true}
				onClick={(e, r) => {
					// first prepare the form (linking in subforms)
					edit.show(r);
				}}
				bulkActions={bulk.bulkActions}
				onBulkAction={bulk.onBulkAction}
			/>
			{edit.render()}
			{createModal.render()}
			{duplicateModal.render()}
			{replaceModal.render()}
		</Page>
	);
}



function collectIds(obj: any, ids: number[]) {
	if (!obj || typeof obj !== "object") return;
	if (typeof obj.id === "number") {
		ids.push(obj.id);
	}
	if (Array.isArray(obj)) {
		for (const item of obj) collectIds(item, ids);
	} else {
		for (const key in obj) {
			if (key === "id") continue;
			collectIds(obj[key], ids);
		}
	}
}

function ReplaceJsonModal(props: {
	obj: { data: any },
	onCancel: () => void,
	onSave: () => void,
}) {
	const { obj } = props;
	const [error, setError] = useState("");

	function handleSave() {
		// if the JSON is a full report_def with a .data attribute, unwrap it
		if (obj.data && typeof obj.data === "object" && obj.data.data) {
			obj.data = obj.data.data;
		}

		const ids: number[] = [];
		collectIds(obj.data, ids);

		// check for duplicate ids
		const seen = new Set<number>();
		const duplicates = new Set<number>();
		for (const id of ids) {
			if (seen.has(id)) duplicates.add(id);
			seen.add(id);
		}

		if (duplicates.size > 0) {
			setError("Duplicate id values found: " + Array.from(duplicates).join(", "));
			return;
		}

		// auto-adjust idCounter if needed
		const maxId = ids.length > 0 ? Math.max(...ids) : 0;
		if (!obj.data.idCounter || obj.data.idCounter <= maxId) {
			obj.data.idCounter = maxId + 1;
		}

		props.onSave();
	}

	return (
		<Modal
			title="Replace JSON"
			size="width-75vw"
			onCancel={props.onCancel}
			onSave={handleSave}
		>
			<div style={{ height: "calc(90vh - 150px)", overflow: "auto" }}>
				<JSEditor prop="data" object={obj} lang="json" />
			</div>
			{error && <div className="bg-error text-inverse" style={{ padding: "8px" }}>{error}</div>}
		</Modal>
	);
}

// export function EditForm(props: {
// 	def: form_def,
// 	onSave: () => void,
// 	onDelete: () => void,
// }) {
// 	const { def } = props;

// 	const formValues = {};

// 	if (!def) return null;

// 	function save() {
// 		// can't save the in-memory structure - it has things that shouldn't be persisted

// 		const obj = {
// 			id: def.id,
// 			title: def.title,
// 			deleted: def.deleted,
// 			data: JSON.parse(JSON.stringify(def.data)),
// 		};

// 		// also check through any subform references and remove the elements[] which were injected
// 		// and remove any unique ids
// 		function scan(e: any) {
// 			if (e.uniqueID) delete e.uniqueID;

// 			if (e.type === "sub-form") {
// 				delete e.elements;
// 			}

// 			if (e.elements) {
// 				for(let child of e.elements) {
// 					scan(child);
// 				}
// 			}
// 		}
// 		scan(obj.data);


// 		api.form_def_post(obj).then(() => {
// 			props.onSave();

// 		}).catch(() => {

// 		});
	
// 	}

// 	return(
// 		<Modal
// 			title="Edit Form"

// 			onSave={save}
// 			onCancel={props.onSave}
// 			cancelOnEscapeKey={false}
// 			saveOnEnterKey={false}

// 			showDelete={true}
// 			onDelete={props.onDelete}
// 		>
// 			<InspectorContainer>
// 				<Presenter
// 					designMode={true}

// 					def={def.data as Element}
// 					refs={def.refs}
// 					subForms={def.subForms}

// 					formValues={formValues}
// 				>
// 					<Inspector />
// 				</Presenter>				
// 			</InspectorContainer>
// 		</Modal>
// 	);
// }